import { useReactiveVar } from "@apollo/client";
import { Link } from "@tanstack/react-router";
import pluralize from "pluralize";
import { useMemo } from "react";

import { user as userStore } from "@/store";

import type { ExperiencesCountFacetType, UserType } from "../types";

interface CountersProps {
  facets: ExperiencesCountFacetType[];
  user: UserType;
}

export default function Counters({ facets, user }: CountersProps) {
  const currentUser = useReactiveVar(userStore);

  const completed = useMemo(
    () => facets.reduce((accum, facet) => accum + facet.count, 0),
    [facets]
  );
  const noCompletedCopy = useMemo(() => {
    if (completed) {
      return null;
    }

    return user.id === currentUser?.id
      ? "Go have some fun, then mark your experience as Done!"
      : `${user.username} doesn’t get out much.`;
  }, [completed, currentUser, user]);

  return (
    <div className="relative rounded-[5px] bg-white/5 transition-colors hover:bg-white/8">
      <div className="flex flex-col gap-2.5 px-3 py-2.5 text-white md:flex-row md:items-center md:gap-7.5 md:px-5 md:py-4">
        <div className="flex flex-nowrap items-center justify-center md:flex-wrap">
          <svg
            aria-hidden="true"
            className="mr-2.5 h-[13px] w-4 text-white md:order-1 md:h-6.5 md:w-8"
            viewBox="0 0 16 13"
          >
            <path
              d="M4.631 12.036l1.602-.172.185.169 8.48-9.24.004-.004c.544-.625.385-1.413-.03-1.875a1.243 1.243 0 00-.82-.426c-.332-.024-.665.107-.944.412L5.566 9.16 2.867 6.25c-.268-.306-.596-.437-.924-.414-.32.023-.608.19-.817.42-.415.453-.58 1.234-.005 1.838l.85.972c.871 1 1.754 2.013 2.66 2.97z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="0.5"
            />
          </svg>
          <small className="whitespace-nowrap text-center font-medium text-15 md:order-3 md:w-full md:text-17">
            <Link
              className="link-overlay"
              to={`/@${user.username}/experiences`}
            >
              Morty score
            </Link>
          </small>
          <strong className="ml-auto font-semibold text-24 md:order-2 md:ml-0 md:text-35">
            {completed}
          </strong>
        </div>
        <ul className="flex w-full flex-col items-stretch gap-1.5 md:gap-[9px]">
          {facets.map(
            ({ category, count }) =>
              count && (
                <li
                  className="flex items-center justify-between leading-[1.2em]"
                  key={category.id}
                >
                  <small className="text-14 text-white/80 md:text-16">
                    {pluralize(category.name)}
                  </small>
                  <strong className="font-semibold text-14 md:text-15">
                    {count}
                  </strong>
                </li>
              )
          )}
          {!!noCompletedCopy && (
            <li className="text-12/[1.5em] text-heather md:text-14/[1.3em]">
              {noCompletedCopy}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
